/**
 * Client for the workstation hardware service's Romanian eID capability.
 *
 * The backend runs centrally — one container for every shop — so it cannot reach a card reader
 * plugged into a counter. The browser is therefore the transport, exactly as it is for label
 * printing (`domain/labelPrinting.ts`): this module fetches from the service on `127.0.0.1` and
 * hands the result to the page.
 *
 * **The PIN and the CAN never leave the workstation.** They go from this page straight to the
 * local service and are used to talk to the chip; the central backend never sees either, which is
 * a property the previous design — a backend that spawned a reader process — could not offer.
 *
 * Pure apart from the injected `fetch`, so every branch is unit-testable.
 */

import type { ReaderIndicator } from './idCardReader';
import {
    HARDWARE_SERVICE_API_VERSION,
    HARDWARE_SERVICE_IDENTITY,
    HARDWARE_SERVICE_ORIGIN,
    type FetchLike,
    type HardwareServiceStatus,
} from './labelPrinting';

/** Capability id for reading the civil record. */
export const CAPABILITY_EID_CIVIL = 'eid.civil';

/** Capability id for reading the cardholder photo. */
export const CAPABILITY_EID_PHOTO = 'eid.photo';

/** How long a card read may take before the page stops waiting. */
export const EID_READ_TIMEOUT_MS = 90_000;

/**
 * Address components, as the service returns them.
 *
 * The card carries one address string; the shape mirrors what the application's form needs.
 */
export interface EidCardAddress {
    county?: string;
    city?: string;
    street?: string;
    streetNumber?: string;
    building?: string;
    entrance?: string;
    floor?: string;
    apartment?: string;
    postalCode?: string;
}

/**
 * What happened to the photo on one read.
 *
 * Four answers where there used to be one. Before the service published this, "no CAN was supplied
 * so no photo was asked for" and "the photo was asked for and failed" were the same empty
 * `photoBase64`, so the page had only the read's overall status to go on — and painted the portrait
 * red on reads where no portrait had ever been requested.
 *
 * `unknown` is not a failure. It is what an older service, or one that cannot tell, produces, and
 * it must render as "not reported": only positively established evidence may tell an operator
 * something went wrong.
 */
export type EidPhotoState = 'not-requested' | 'read' | 'failed' | 'unknown';

/** The closed set of codes the photo status may carry. One code per state, one sentence per code. */
export type EidPhotoCode =
    /** No CAN was supplied, so the photo was never asked for. The normal case, never an error. */
    | 'PHOTO_NOT_REQUESTED'
    /** An image came back. */
    | 'PHOTO_READ'
    /** PACE authentication failed — typically a CAN that does not belong to this card. */
    | 'PACE_FAILED'
    /** PACE succeeded and the applet was selected, but DG2 would not read. */
    | 'DG2_UNREADABLE'
    /**
     * EF.CardAccess could not be obtained or decoded, so PACE never ran.
     *
     * Split out of `DG2_UNREADABLE` on 2026-09-09. It has to be its own code because
     * `DG2_UNREADABLE`'s sentence tells the operator the card accepted the CAN — which, on this
     * failure, the service has no evidence for at all.
     */
    | 'CARD_ACCESS_UNAVAILABLE'
    /** PACE succeeded and the card's eMRTD application would not select. */
    | 'EMRTD_UNAVAILABLE'
    /** The card returned the photo file and the workstation service could not decode it. */
    | 'DG2_UNPARSABLE'
    /** The photo did not come off the card the civil record came from, so it was dropped. */
    | 'PHOTO_CARD_CHANGED'
    /**
     * The photo could not be *proven* to come off that card, so it was not used.
     *
     * Not the same statement as `PHOTO_CARD_CHANGED`, and never to be merged with it: that one
     * says the service checked and the cards disagree, this one says it could not check at all.
     * Telling an operator their card changed when the zone was merely unreadable sends them
     * looking for a swap that may never have happened.
     */
    | 'PHOTO_CARD_UNVERIFIED'
    /** The service did not say, which is not the same as saying it failed. */
    | 'PHOTO_UNKNOWN';

/** The bounded pair the service publishes for a read's photo. */
export interface EidPhotoStatus {
    state: EidPhotoState;
    code: EidPhotoCode;
}

/** What an absent, malformed or unrecognised photo block means: not reported, never failed. */
export const PHOTO_STATUS_NOT_REPORTED: EidPhotoStatus = {
    state: 'unknown',
    code: 'PHOTO_UNKNOWN',
};

/** The states this page has a sentence for. */
const PHOTO_STATES: ReadonlySet<string> = new Set<EidPhotoState>([
    'not-requested',
    'read',
    'failed',
    'unknown',
]);

/** The codes this page has a sentence for. */
const PHOTO_CODES: ReadonlySet<string> = new Set<EidPhotoCode>([
    'PHOTO_NOT_REQUESTED',
    'PHOTO_READ',
    'PACE_FAILED',
    'DG2_UNREADABLE',
    'CARD_ACCESS_UNAVAILABLE',
    'EMRTD_UNAVAILABLE',
    'DG2_UNPARSABLE',
    'PHOTO_CARD_CHANGED',
    'PHOTO_CARD_UNVERIFIED',
    'PHOTO_UNKNOWN',
]);

/** The one state that names a portrait the service stands behind. */
const PHOTO_STATE_READ: EidPhotoState = 'read';

/** The one code that names it. Both must agree; either alone is a contradiction. */
const PHOTO_CODE_READ: EidPhotoCode = 'PHOTO_READ';

/** Why portrait bytes are, or are not, trusted for use by this application. */
export type PortraitTrustReason =
    /** There were no usable bytes to begin with. */
    | 'no-bytes'
    /** No status block at all - an older service. The one case bytes are trusted without one. */
    | 'legacy-status-absent'
    /** The status block names success, in both halves. */
    | 'authorised'
    /** The status block is well formed and refuses the portrait. */
    | 'status-refuses'
    /** The status block is present and cannot be read as an authorisation. */
    | 'status-unreadable';

/** Whether portrait bytes may be rendered, applied or stored, and the bytes if so. */
export interface PortraitTrust {
    readonly trusted: boolean;
    readonly reason: PortraitTrustReason;
    /** The bytes to use, or the empty string. Never the rejected bytes. */
    readonly base64: string;
}

/**
 * Decides whether portrait bytes may be used, from the bytes and the raw status block together.
 *
 * <b>Why this exists at all.</b> The service cannot currently send a portrait alongside a status
 * that refuses one: every failure path builds its response from a null photo, and the service suite
 * holds that. This is the browser's own check, made because the browser is where the bytes become a
 * face on a screen, a field in a form and a row in a database - and because "the sender promises
 * not to" is not a property this side can verify.
 *
 * <b>It takes the RAW block, not a parsed status.</b> {@link parseEidPhotoStatus} deliberately
 * collapses an absent block and a malformed one into the same "not reported" value, which is right
 * for choosing a sentence and wrong for choosing trust: one is an older service that predates the
 * block, the other is a response this application cannot read. The first keeps its portrait; the
 * second does not.
 *
 * <b>Only an absent key is the legacy exception.</b> An explicit `null` is a service that has the
 * block and is saying nothing with it, which is not an authorisation.
 *
 * <b>Both halves must name success.</b> A `read` state with any other code, or `PHOTO_READ` with
 * any other state, is a response contradicting itself, and a contradiction is never resolved in
 * favour of showing a face.
 */
export function derivePortraitTrust(rawPhoto: unknown, photoBase64: unknown): PortraitTrust {
    const bytes = typeof photoBase64 === 'string' ? photoBase64.trim() : '';

    const refuse = (reason: PortraitTrustReason): PortraitTrust => ({
        trusted: false,
        reason,
        base64: '',
    });

    if (bytes.length === 0) {
        return refuse('no-bytes');
    }

    if (rawPhoto === undefined) {
        return { trusted: true, reason: 'legacy-status-absent', base64: bytes };
    }

    if (rawPhoto === null || typeof rawPhoto !== 'object') {
        return refuse('status-unreadable');
    }

    const { state, code } = rawPhoto as { state?: unknown; code?: unknown };

    if (typeof state !== 'string' || typeof code !== 'string') {
        return refuse('status-unreadable');
    }

    if (!PHOTO_STATES.has(state) || !PHOTO_CODES.has(code)) {
        return refuse('status-unreadable');
    }

    const stateNamesSuccess = state === PHOTO_STATE_READ;

    if (stateNamesSuccess !== (code === PHOTO_CODE_READ)) {
        return refuse('status-unreadable');
    }

    return stateNamesSuccess
        ? { trusted: true, reason: 'authorised', base64: bytes }
        : refuse('status-refuses');
}

/**
 * Turns a service's photo block into a status, or refuses it.
 *
 * Verified rather than cast, for the same reason the reader listing is: an unverified body would let
 * a service answering `{}` reach the screen as a definite statement about a photo. Anything this
 * page cannot fully understand — an absent block, a non-object, a state or a code it has no sentence
 * for — becomes {@link PHOTO_STATUS_NOT_REPORTED}, because a page that renders a code it cannot
 * translate shows an operator a raw token, and a page that guesses at an unknown state may show
 * them a failure that never happened.
 */
export function parseEidPhotoStatus(value: unknown): EidPhotoStatus {
    if (value === null || typeof value !== 'object') {
        return PHOTO_STATUS_NOT_REPORTED;
    }

    const { state, code } = value as { state?: unknown; code?: unknown };

    if (typeof state !== 'string' || typeof code !== 'string') {
        return PHOTO_STATUS_NOT_REPORTED;
    }

    if (!PHOTO_STATES.has(state) || !PHOTO_CODES.has(code)) {
        return PHOTO_STATUS_NOT_REPORTED;
    }

    return { state: state as EidPhotoState, code: code as EidPhotoCode };
}

/** The i18n key for the sentence explaining a photo outcome. One per code, so the set is closed. */
export function eidPhotoMessageKey(status: EidPhotoStatus): string {
    return `idCardReader.photo.${status.code}`;
}

/** The civil record and, when a CAN was supplied and the card allowed it, the photo. */
export interface EidCardData {
    cnp: string;
    lastName: string;
    firstName: string;
    dateOfBirth: string;
    sex: string;
    nationality: string;
    placeOfBirth: string;
    rawAddress: string;
    documentNumber: string;
    issuer: string;
    issuedOn: string;
    validUntil: string;
    /** Base64 JPEG, or empty when no photo was read. */
    photoBase64: string;
    cardPresent: boolean;
    pinVerified: boolean;
    pinRequired: boolean;
    civilDataRead: boolean;
    /**
     * What happened to the photo.
     *
     * Always present from a service that publishes it, and absent from every older one — which is
     * why it is optional here and why {@link parseEidPhotoStatus} is what reads it. `photoBase64`
     * keeps its own meaning untouched; this says what that empty string means.
     */
    photo?: EidPhotoStatus;
}

/**
 * Why a read did not produce a card.
 *
 * These are the service's own codes, passed through rather than reinterpreted, plus the two this
 * client adds for conditions the service never gets to report.
 */
export type EidErrorCode =
    /** Nothing answered on the loopback port — the service is not installed or not running. */
    | 'SERVICE_UNAVAILABLE'
    /** Something answered but is not this service, or speaks another API version. */
    | 'SERVICE_UNRECOGNISED'
    /** This build of the service cannot read cards on this platform. */
    | 'EID_NOT_IMPLEMENTED'
    /** The read took longer than {@link EID_READ_TIMEOUT_MS}, or the page cancelled it. */
    | 'READ_TIMEOUT'
    | 'NO_READER'
    | 'NO_CARD'
    | 'CARD_BUSY'
    | 'PIN_REQUIRED'
    | 'PIN_REJECTED'
    | 'PIN_BLOCKED'
    | 'VERIFY_REFUSED'
    | 'CIVIL_FILES_REFUSED'
    | 'NO_CIVIL_DATA'
    | 'MISSING_CREDENTIALS'
    | 'MISSING_CAN'
    | 'PACE_FAILED'
    | 'PHOTO_UNAVAILABLE'
    /**
     * The three outcomes split out of `PHOTO_UNAVAILABLE` on 2026-09-09.
     *
     * A service older than that answers `PHOTO_UNAVAILABLE` for all of them, which stays in the
     * set above, so a workstation that has not been updated keeps its existing sentence.
     */
    | 'CARD_ACCESS_UNAVAILABLE'
    | 'EMRTD_UNAVAILABLE'
    | 'DG2_UNPARSABLE'
    | 'CARD_CHANGED'
    /**
     * Which reader to use, when the workstation has more than one.
     *
     * These four cannot occur on a workstation with a single chip reader, which is every
     * workstation in the field today: before device selection existed, the service read from
     * whichever reader the PC/SC resource manager listed first. They are here so a counter that
     * grows a second reader gets a sentence telling the operator what to do, rather than the
     * generic failure an unrecognised code renders as.
     */
    | 'DEVICE_SELECTION_REQUIRED'
    | 'SELECTED_DEVICE_UNAVAILABLE'
    | 'SELECTED_DEVICE_UNKNOWN'
    | 'DEVICE_IDENTITY_CONFLICT'
    /**
     * The selected reader is in the middle of another operation on this workstation.
     *
     * Transient, and the only code here that resolves itself: the operator reads again when the
     * other operation finishes. Deliberately not retried automatically — a retry loop against a
     * reader somebody else is using is how two operations end up fighting over one card.
     */
    | 'DEVICE_BUSY'
    | 'ERROR';

/** What a read produced. A discriminated union, so a caller cannot read a card it did not get. */
export type EidReadResult = { ok: true; card: EidCardData } | { ok: false; code: EidErrorCode };

/** Body of the service's error form. */
interface EidErrorBody {
    error?: string;
    message?: string;
}

/** The codes the service is allowed to hand back, so an unknown one cannot masquerade as handled. */
const SERVICE_CODES: ReadonlySet<string> = new Set<EidErrorCode>([
    'EID_NOT_IMPLEMENTED',
    'NO_READER',
    'NO_CARD',
    'CARD_BUSY',
    'PIN_REQUIRED',
    'PIN_REJECTED',
    'PIN_BLOCKED',
    'VERIFY_REFUSED',
    'CIVIL_FILES_REFUSED',
    'NO_CIVIL_DATA',
    'MISSING_CREDENTIALS',
    'MISSING_CAN',
    'PACE_FAILED',
    'PHOTO_UNAVAILABLE',
    'CARD_ACCESS_UNAVAILABLE',
    'EMRTD_UNAVAILABLE',
    'DG2_UNPARSABLE',
    'CARD_CHANGED',
    'DEVICE_SELECTION_REQUIRED',
    'SELECTED_DEVICE_UNAVAILABLE',
    'SELECTED_DEVICE_UNKNOWN',
    'DEVICE_IDENTITY_CONFLICT',
    'DEVICE_BUSY',
    'ERROR',
]);

/**
 * Whether a ready service advertises the civil read.
 *
 * The photo is deliberately *not* required: the service advertises it separately, and a photo the
 * card refuses is reported per read rather than by hiding the button.
 */
export function canReadEid(status: HardwareServiceStatus): boolean {
    return status.state === 'ready' && status.health.capabilities.includes(CAPABILITY_EID_CIVIL);
}

/** Whether a ready service advertises the photo, so a page can decide whether to ask for a CAN. */
export function canReadEidPhoto(status: HardwareServiceStatus): boolean {
    return status.state === 'ready' && status.health.capabilities.includes(CAPABILITY_EID_PHOTO);
}

/**
 * Read the card: the civil record, plus the photo when a CAN is given.
 *
 * @param fetchImpl The browser's fetch.
 * @param credentials The PIN, and the CAN when the operator wants the photo. Neither is logged,
 * neither is placed in a URL, and neither reaches the central backend.
 * @param status The probed service, so a read is not attempted against something that cannot serve
 * it. Omit only in tests.
 *
 * **Never throws.** Every failure is a code the caller can turn into a sentence, because a thrown
 * error at a counter becomes "something went wrong" on an operator's screen.
 *
 * **Never retries.** A retry costs the operator a card cycle and, on this card, cannot succeed
 * anyway: reading claims one of the two applications for the rest of the insertion. The service
 * does its own bounded retry internally, where it can power-cycle the card between attempts.
 *
 * **Never falls back to another implementation.** A failed read means this workstation could not
 * read this card; asking a different reader on the same insertion answers a different question.
 */
export async function readEidCard(
    fetchImpl: FetchLike,
    credentials: { pin: string; can?: string },
    status?: HardwareServiceStatus,
): Promise<EidReadResult> {
    if (status !== undefined && !canReadEid(status)) {
        return { ok: false, code: unusableServiceCode(status) };
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), EID_READ_TIMEOUT_MS);
    let result: EidReadResult;

    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/eid/read-full`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Body only. The PIN and CAN must never appear in a URL, where they would reach the
            // browser's history and any proxy log on the way.
            body: JSON.stringify({ pin: credentials.pin, can: credentials.can ?? null }),
            signal: controller.signal,
        });

        result = await interpret(response);
    } catch (error) {
        // An abort is our own timeout firing; anything else is the service not being there.
        result = { ok: false, code: isAbort(error) ? 'READ_TIMEOUT' : 'SERVICE_UNAVAILABLE' };
    }

    // Deliberately not a `finally`: both paths above assign and fall through to here, and the
    // synthetic branch a `finally` adds cannot be reached by any test, which would leave the
    // project's 100% branch gate unmeetable for an artifact rather than for a real path.
    clearTimeout(timeout);
    return result;
}

/** Turn the service's response into a result. */
async function interpret(response: Response): Promise<EidReadResult> {
    let body: unknown;

    try {
        body = await response.json();
    } catch {
        return { ok: false, code: 'ERROR' };
    }

    if (response.ok) {
        const card = body as EidCardData;
        const rawPhoto = (body as { photo?: unknown })?.photo;

        // The trust boundary, and the only place portrait bytes are judged. Rejected bytes are
        // dropped HERE rather than guarded at each consumer: the modal, the Add Client form, the
        // upload and the identity envelope all treat an empty portrait as no portrait already, so
        // removing the bytes once is what makes every one of them safe without a condition each.
        // The status itself is passed through untouched - the tile still explains what happened and
        // the audit still records exactly what the service said.
        const portrait = derivePortraitTrust(rawPhoto, card.photoBase64);

        // Verified on the way in, so nothing downstream has to wonder whether the block was there,
        // whether it was well formed, or whether this service is old enough to omit it entirely.
        return {
            ok: true,
            card: {
                ...card,
                photoBase64: portrait.base64,
                photo: parseEidPhotoStatus(rawPhoto),
            },
        };
    }

    const code = (body as EidErrorBody)?.error;

    // An unrecognised code is reported as a generic failure rather than passed through: the page
    // maps codes to sentences, and an unmapped one would render as a raw token to an operator.
    return { ok: false, code: SERVICE_CODES.has(code ?? '') ? (code as EidErrorCode) : 'ERROR' };
}

/** Why a probed service cannot serve a read. */
function unusableServiceCode(status: HardwareServiceStatus): EidErrorCode {
    if (status.state === 'unavailable') {
        return 'SERVICE_UNAVAILABLE';
    }

    if (status.state === 'ready') {
        // Reachable and current, but this build has no card transport for the platform — the macOS
        // case today.
        return 'EID_NOT_IMPLEMENTED';
    }

    return 'SERVICE_UNRECOGNISED';
}

function isAbort(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

/**
 * The i18n key explaining a failed read to the operator.
 *
 * Every code gets its own sentence, because the useful ones are the ones that tell an operator what
 * to *do*: re-seat the card, check the CAN, or stop retrying because the PIN counter is spent. The
 * distinction that matters most is `PIN_REJECTED` from `VERIFY_REFUSED` — the first means the card
 * compared the PIN and said no, the second that it never compared it at all, and conflating them
 * once sent operators hunting for a problem with a PIN that was correct.
 */
export function eidErrorMessageKey(code: EidErrorCode): string {
    return `idCardReader.error.${code}`;
}

/**
 * Turn a probed service into the reader indicator the modal shows.
 *
 * This replaced a probe of the *backend's* PC/SC, which in a central deployment reported whether a
 * card was in a reader plugged into the server — a question no operator ever wanted answered.
 *
 * It deliberately says nothing about whether a card is inserted. Card presence cannot be known
 * without connecting to the card, and connecting to check is exactly what once stole the card from
 * a read in progress. The honest indicator is "the workstation can read"; whether a card is in the
 * slot is answered by reading.
 */
export function deriveEidIndicator(status: HardwareServiceStatus | null): ReaderIndicator {
    if (status === null) {
        return { level: 'warning', messageKey: 'idCardReader.statusChecking', canRead: false };
    }

    if (status.state === 'unavailable') {
        return { level: 'error', messageKey: 'idCardReader.statusServiceStopped', canRead: false };
    }

    if (status.state === 'unrecognised') {
        return {
            level: 'error',
            messageKey: 'idCardReader.statusServiceUnrecognised',
            canRead: false,
        };
    }

    if (status.state === 'incompatible') {
        return {
            level: 'error',
            messageKey: 'idCardReader.statusServiceIncompatible',
            canRead: false,
        };
    }

    // An unconnected workstation reports nothing about its hardware, so there is no
    // capability list to consult and nothing to say beyond "connect this workstation".
    if (status.state === 'unpaired') {
        return { level: 'error', messageKey: 'idCardReader.statusNotPaired', canRead: false };
    }

    if (!canReadEid(status)) {
        return { level: 'error', messageKey: 'idCardReader.statusNoReaderSupport', canRead: false };
    }

    return { level: 'success', messageKey: 'idCardReader.statusReady', canRead: true };
}

/** How far a read has got, as the modal tracks it. */
export type EidReadPhase = 'idle' | 'reading' | 'success' | 'error';

/**
 * How a chip data-group tile is painted.
 *
 * `error` is reachable for the civil record and **never** for the portrait. That asymmetry is the
 * whole point: a failed read is a failure of the record the operator asked for, and the portrait —
 * which they may not have asked for at all — must not be coloured by it.
 */
export type ChipTileTone = 'success' | 'processing' | 'warning' | 'error' | 'neutral';

/** One data-group tile: how it looks, what its tag says, and the sentence beneath it. */
export interface ChipTile {
    tone: ChipTileTone;
    /** i18n key for the tag. */
    labelKey: string;
    /** i18n key for the sentence. */
    descriptionKey: string;
}

/**
 * The civil-record tile.
 *
 * This one *does* inherit the read's overall status, because the civil record is what a failed read
 * failed to produce. The DATA tile is where a failure belongs.
 */
export function deriveDataTile(civilDataRead: boolean, phase: EidReadPhase): ChipTile {
    if (civilDataRead) {
        return {
            tone: 'success',
            labelKey: 'idCardReader.dgStatusRead',
            descriptionKey: 'idCardReader.dataDescription',
        };
    }

    if (phase === 'error') {
        return {
            tone: 'error',
            labelKey: 'idCardReader.dgStatusError',
            descriptionKey: 'idCardReader.dataDescription',
        };
    }

    return {
        tone: phase === 'reading' ? 'processing' : 'neutral',
        labelKey:
            phase === 'reading' ? 'idCardReader.dgStatusReading' : 'idCardReader.dgStatusPending',
        descriptionKey: 'idCardReader.dataDescription',
    };
}

/**
 * The portrait tile, derived from the **photo** status and never from the read's overall status.
 *
 * That sentence is the defect being fixed. A read that fails overall — a rejected PIN, a card
 * removed mid-read — used to paint this tile red even when no CAN had been entered and no portrait
 * had ever been requested, because the tile had nothing else to go on. It now says only what the
 * service reported about the photo, and when the service reported nothing it says exactly that.
 *
 * A photo failure is a **warning**, not an error: the operator still has the civil record they came
 * for, and the read as a whole did not fail.
 */
export function deriveDg2Tile(status: EidPhotoStatus | null, phase: EidReadPhase): ChipTile {
    if (phase === 'reading') {
        return {
            tone: 'processing',
            labelKey: 'idCardReader.dgStatusReading',
            descriptionKey: 'idCardReader.dg2Description',
        };
    }

    if (phase === 'idle') {
        return {
            tone: 'neutral',
            labelKey: 'idCardReader.dgStatusPending',
            descriptionKey: 'idCardReader.dg2Description',
        };
    }

    // A read that failed overall carries no photo block, because the service answered with an
    // error rather than a record. Nothing is known about the photo, and "nothing is known" is
    // reported as such rather than as a failure.
    const reported = status ?? PHOTO_STATUS_NOT_REPORTED;
    const descriptionKey = eidPhotoMessageKey(reported);

    if (reported.state === 'read') {
        return { tone: 'success', labelKey: 'idCardReader.dgStatusRead', descriptionKey };
    }

    if (reported.state === 'failed') {
        return { tone: 'warning', labelKey: 'idCardReader.dgStatusPhotoFailed', descriptionKey };
    }

    if (reported.state === 'not-requested') {
        return { tone: 'neutral', labelKey: 'idCardReader.dgStatusNotRequested', descriptionKey };
    }

    return { tone: 'neutral', labelKey: 'idCardReader.dgStatusNotReported', descriptionKey };
}

/**
 * How long a reader listing may take before the page stops waiting.
 *
 * Shorter than a read by an order of magnitude, and deliberately so: enumerating readers touches no
 * card and returns as fast as the resource manager answers, so a listing still running after
 * fifteen seconds is a service that has stopped answering rather than a slow operator.
 */
export const EID_READER_LIST_TIMEOUT_MS = 15_000;

/** One chip reader, as the service describes it. The optical record's twin, field for field. */
export interface DetectedChipReader {
    id: string;
    provider: string;
    displayName: string;
    model: string | null;
    /** Four characters at most. The full serial never leaves the workstation service. */
    serialSuffix: string | null;
    transport: string;
    connected: boolean;
    evidence: string;
    selected: boolean;
    preferred: boolean;
    busy: boolean;
    statusReason: string | null;
}

/** Body of `GET /v1/eid/readers` — what is attached, and which one this workstation would use. */
export interface ChipReaderState {
    readers: DetectedChipReader[];
    /** The reader a read carrying no device id would use. Null whenever that is ambiguous. */
    selectedDeviceId: string | null;
    /** The reader an operator chose, attached or not. A recommendation, never a substitute. */
    preferredDeviceId: string | null;
    selectionSource: string;
    selectionRequired: boolean;
    ready: boolean;
    code: string | null;
    statusReason: string | null;
    observedAt: string;
}

/**
 * Why a reader listing did not produce an inventory.
 *
 * None of these is ever rendered. They exist so a failure can be told apart, in a test and in a log,
 * from the one thing it must never be confused with — a workstation with no reader attached.
 */
export type ChipReaderListErrorCode =
    /** Nothing answered on the loopback port. */
    | 'SERVICE_UNAVAILABLE'
    /** This build of the service cannot read cards on this platform. */
    | 'EID_NOT_IMPLEMENTED'
    /** The listing took longer than {@link EID_READER_LIST_TIMEOUT_MS}. */
    | 'REQUEST_TIMEOUT'
    /** The service answered with something this client cannot verify against the contract. */
    | 'UNEXPECTED_RESPONSE';

/** Either the inventory, or why there is none. */
export type ChipReaderListing =
    | { ok: true; value: ChipReaderState }
    | { ok: false; code: ChipReaderListErrorCode };

/** A field that must be present as text. */
const isText = (value: unknown): boolean => typeof value === 'string';

/** A field that must be present as a boolean. */
const isFlag = (value: unknown): boolean => typeof value === 'boolean';

/** A field the contract declares nullable: text or an explicit null, never absent. */
const isOptionalText = (value: unknown): boolean => value === null || typeof value === 'string';

/** Every field one reader record must carry, and what it must be. */
const READER_SHAPE: Readonly<Record<string, (value: unknown) => boolean>> = {
    id: isText,
    provider: isText,
    displayName: isText,
    model: isOptionalText,
    serialSuffix: isOptionalText,
    transport: isText,
    connected: isFlag,
    evidence: isText,
    selected: isFlag,
    preferred: isFlag,
    busy: isFlag,
    statusReason: isOptionalText,
};

/** Every field the listing itself must carry, beside `readers`. */
const LISTING_SHAPE: Readonly<Record<string, (value: unknown) => boolean>> = {
    selectedDeviceId: isOptionalText,
    preferredDeviceId: isOptionalText,
    selectionSource: isText,
    selectionRequired: isFlag,
    ready: isFlag,
    code: isOptionalText,
    statusReason: isOptionalText,
    observedAt: isText,
};

/** Whether one value carries every field of a shape, as that shape declares it. */
function matches(value: unknown, shape: Readonly<Record<string, (value: unknown) => boolean>>) {
    if (value === null || typeof value !== 'object') {
        return false;
    }

    const fields = value as Record<string, unknown>;

    return Object.entries(shape).every(([field, holds]) => holds(fields[field]));
}

/**
 * Turns a listing body into an inventory, or refuses it.
 *
 * Verified rather than cast, which is the whole point of this function: an unverified body would
 * make a service answering `{}` indistinguishable from a counter with no reader plugged in, and the
 * screen would then tell an operator to attach a reader that is already attached.
 */
export function parseChipReaders(body: unknown): ChipReaderState | null {
    if (!matches(body, LISTING_SHAPE)) {
        return null;
    }

    const listed = (body as { readers?: unknown }).readers;

    if (!Array.isArray(listed) || !listed.every(reader => matches(reader, READER_SHAPE))) {
        return null;
    }

    return body as ChipReaderState;
}

/** Performs one listing call and turns every way it can end into a value. */
async function deliver(
    send: () => Promise<Response>,
    unreachable: () => ChipReaderListErrorCode,
): Promise<ChipReaderListing> {
    let response: Response;

    try {
        response = await send();
    } catch {
        return { ok: false, code: unreachable() };
    }

    if (!response.ok) {
        // 501 is the platform saying it has no card transport at all — worth keeping apart from a
        // service that answered something this client could not use.
        return {
            ok: false,
            code: response.status === 501 ? 'EID_NOT_IMPLEMENTED' : 'UNEXPECTED_RESPONSE',
        };
    }

    let body: unknown;

    try {
        body = await response.json();
    } catch {
        return { ok: false, code: 'UNEXPECTED_RESPONSE' };
    }

    const state = parseChipReaders(body);

    return state === null ? { ok: false, code: 'UNEXPECTED_RESPONSE' } : { ok: true, value: state };
}

/** One call to the service's reader endpoints, bounded and verified. */
async function listing(
    fetchImpl: FetchLike,
    path: string,
    init: RequestInit,
): Promise<ChipReaderListing> {
    const controller = new AbortController();
    let expired = false;
    const timeout = setTimeout(() => {
        expired = true;
        controller.abort();
    }, EID_READER_LIST_TIMEOUT_MS);

    const outcome = await deliver(
        () =>
            fetchImpl(`${HARDWARE_SERVICE_ORIGIN}${path}`, { ...init, signal: controller.signal }),
        () => (expired ? 'REQUEST_TIMEOUT' : 'SERVICE_UNAVAILABLE'),
    );

    // Cleanup after the await rather than in a `finally`, because `deliver` is written not to throw:
    // every way this call can end already comes back as a value.
    clearTimeout(timeout);

    return outcome;
}

/**
 * What chip readers this workstation has.
 *
 * Describes, never resolves: PC/SC enumerates readers without connecting to a card, so asking this
 * on a timer cannot disturb a read in progress. That distinction is the reason a screen may state
 * whether a *reader* is attached while still saying nothing about whether a *card* is in it.
 */
export function listChipReaders(fetchImpl: FetchLike): Promise<ChipReaderListing> {
    return listing(fetchImpl, '/v1/eid/readers', { method: 'GET' });
}

/**
 * Records which chip reader this workstation uses, or clears the choice.
 *
 * Stored by the service, in its own protected state, beside the optical reader's. Never in the
 * browser: local storage is per-origin, per-profile and cleared by anyone who clears their browsing
 * data, which would silently return a two-reader counter to "choose again" in the middle of a shift.
 */
export function selectChipReader(
    fetchImpl: FetchLike,
    deviceId: string | null,
): Promise<ChipReaderListing> {
    return listing(fetchImpl, '/v1/eid/readers/select', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId }),
    });
}

/** The identity constants, re-exported so a page needs one import to probe and to read. */
export { HARDWARE_SERVICE_API_VERSION, HARDWARE_SERVICE_IDENTITY, HARDWARE_SERVICE_ORIGIN };
